/*
 * Copyright (C) 2005 - 2023. Cloud Software Group, Inc. All Rights Reserved. Confidential & Proprietary.
 * Licensed pursuant to commercial Cloud Software Group, Inc. End User License Agreement.
 */

import _ from 'underscore';
import $ from 'jquery';
import React, { RefObject } from 'react';
import { AxisDescriptors, ICrosstabDataPartProps, IInternalCrosstabDataCell, } from '../types/CrosstabVisualizationTypes';
import { IAxisItem } from '../../../../store/types/StateTypes';
import formattersFactory from '../../../../formatters/formattersFactory';
import { ILinkToRefObjectPair } from '../../types/commonTypes';
import hyperlinkHelper from './hyperlinkHelper';
// @ts-ignore
import i18n from '../../../../../i18n/AdHocBundle.properties';
import AdHocMessageComponent from '../../../AdHocMessageComponent';

function classNamesByType(): { [_: string]: string } {
    return {
        boolean: 'jr jr-mDatatable-cell-text',
        byte: 'jr jr-mDatatable-cell-number',
        short: 'jr jr-mDatatable-cell-number',
        integer: 'jr jr-mDatatable-cell-number',
        long: 'jr jr-mDatatable-cell-number',
        bigInteger: 'jr jr-mDatatable-cell-number',
        float: 'jr jr-mDatatable-cell-number',
        double: 'jr jr-mDatatable-cell-number',
        bigDecimal: 'jr jr-mDatatable-cell-number',
        string: 'jr jr-mDatatable-cell-text',
        date: 'jr jr-mDatatable-cell-date',
        timestamp: 'jr jr-mDatatable-cell-date',
        time: 'jr jr-mDatatable-cell-date',
        loading: 'jr jr-mDatatable-cell-loading'
    }
}

function formatValue(value: string, cellProperties?: IAxisItem) {
    if (value === null) {
        return '';
    }

    if (!cellProperties) {
        return value;
    }

    if (!cellProperties.aggregationType) {
        return value;
    }

    if (cellProperties.categorizer) {
        return formattersFactory(cellProperties.aggregationType).format(value, cellProperties.aggregationFormat, {
            categorizer: cellProperties.categorizer,
            useUserTimezone: true
        });
    }

    return formattersFactory(cellProperties.aggregationType).format(value, cellProperties.aggregationFormat);
}

function makeClassName(axisItem?: IAxisItem): string {
    const className = 'jr jr-mDatatable-cell-text';
    if (!axisItem) {
        return className;
    }

    if (!axisItem.aggregationType) {
        return className;
    }

    return classNamesByType()[axisItem.aggregationType];
}

export function cellFactory(cell: IInternalCrosstabDataCell, axisItem?: IAxisItem) {
    /* eslint-disable react/jsx-closing-tag-location */
    if (cell.loading) {
        return (
            <div
                className="jr jr-mDatatable-skeleton-cell-text"
                style={{
                    width: '50%',
                    top: '8px'
                }}
            >&nbsp;</div>
        )
    }

    const formattedValue = formatValue(cell.label, axisItem);
    const className = makeClassName(axisItem);

    return (
        <div
            role="cell"
            className={className}
            onFocus={() => {}}
        >{formattedValue}</div>
    );
    /* eslint-enable react/jsx-closing-tag-location */
}

const measureForCell = (descriptors: AxisDescriptors, rIndex: number, cIndex: number) => (
    descriptors.rowDescriptors.rowMeasures[rIndex] || descriptors.columnDescriptors.columnMeasures[cIndex]
);

export class CrosstabDataPart extends React.Component<ICrosstabDataPartProps> {

    bodyRef: any

    hyperlinkEnabledFor: Array<string>

    constructor(props :any) {
        super(props);

        this.hyperlinkEnabledFor = [];
        this.hyperlinkEventListener = this.hyperlinkEventListener.bind(this);
    }

    componentDidMount() {
        const {
            linkOptions
        } = this.props;

        if (linkOptions && linkOptions.events && this.bodyRef.current) {
            const $body :any = $(this.bodyRef.current);
            Object.keys(linkOptions.events).forEach((eventName: string) => {
                this.hyperlinkEnabledFor.push(eventName);
                $body.on(eventName, '.jr-jHyperLink', this.hyperlinkEventListener);
            });
        }
    }

    componentWillUnmount() {
        const $body = $(this.bodyRef.current);
        for (let i = 0; i < this.hyperlinkEnabledFor.length; i += 1) {
            $body.off(this.hyperlinkEnabledFor[i], '.jr-jHyperLink');
        }
    }

    getHyperlinkData(rowIndex: number, columnIndex: number) {
        const {
            rowNodes,
            columnNodes,
            levelsStructure
        } = this.props;

        if (!rowNodes[rowIndex] || !columnNodes[columnIndex]) {
            return {};
        }

        const nodesFromRowHeader = rowNodes[rowIndex].nodes;
        const nodesFromColumnHeader = columnNodes[columnIndex].nodes;

        const columnLinkData = hyperlinkHelper.getHyperlinkDataFromNodes({
            levelsStructure,
            type: 'column',
            nodes: nodesFromColumnHeader
        });
        const rowLinkData = hyperlinkHelper.getHyperlinkDataFromNodes({
            levelsStructure,
            type: 'row',
            nodes: nodesFromRowHeader,
        });

        return _.extend({}, columnLinkData, rowLinkData);
    }

    hyperlinkEventListener(event: React.MouseEvent<HTMLDivElement>) {
        const {
            linkOptions
        } = this.props;

        if (!linkOptions || !linkOptions.events) {
            return;
        }

        const callback = linkOptions.events[event.type];
        if (!callback) {
            return;
        }

        const $cell = $(event.currentTarget);
        const rowIndex = $cell.data('row-index');
        const columnIndex = $cell.data('column-index');

        const linkData = this.getHyperlinkData(rowIndex, columnIndex);

        const $Event = new $.Event((event as unknown) as string);

        callback.call(event.currentTarget, $Event, linkData);
    }

    render() {
        const {
            data,
            descriptors,
            empty,
            linkOptions,
            hyperlinksCollector
        } = this.props;

        const collectCells = (linkOptions && linkOptions.beforeRender);
        const hyperlinksRefs :ILinkToRefObjectPair[] = [];
        this.bodyRef = React.createRef();

        data.forEach((_row, i) => {
            const row = _row,
                nextRow = data[i + 1];
            if (nextRow && nextRow.totalRow && !row.totalRow) {
                row.lastRowValue = true;
            }
        });

        const dataRows = data.map((row, rIndex) => {
            let rowClassName = 'jr-mDatatable-row ';
            rowClassName += row.totalRow ? 'jr-mDatatable-rowTotal ' : 'jr-mDatatable-rowValue ';
            rowClassName += row.lastRowValue ? 'jr-mDatatable-rowValueLast ' : '';
            rowClassName += (rIndex + 1) % 2 === 0 ? 'jr-mDatatable-rowEven ' : 'jr-mDatatable-rowOdd ';
            rowClassName += 'jr';
            return (
                <tr key={row.key} className={rowClassName}>
                    {row.cells.map((cell, cIndex: number) => {
                        const cellClassNames = ['jr-mDatatable-cell', 'jr-mDatatable-cellValue', 'jr'];
                        if (row.totalRow || cell.isTotal) {
                            cellClassNames.push('jr-mDatatable-cellTotal');
                        }
                        if (linkOptions) {
                            cellClassNames.push('jr-mDatatable-cell-hyperlink');
                            cellClassNames.push('jr-jHyperLink');
                            cellClassNames.push('jr-jHyperLink-body');
                        }

                        const divClassNames = ['jr-mDatatable-cell-wrapper', 'jr'];

                        if (cell.loading) {
                            cellClassNames.push('jr-mDatatable-skeleton-cell');
                            divClassNames.push('jr-mDatatable-skeleton-body-cell-wrapper');
                        }

                        const cellStyles = {
                            minWidth: descriptors.columnDescriptors.widths[cIndex]
                        };

                        // we need to give to beforeRender() callback the DOM node of the cell,
                        // and to do this we need a 'ref' object.
                        const ref: RefObject<any> = React.createRef();
                        // and we also don't need to actually do anything if user didn't provide
                        // the beforeRender callback
                        if (!cell.loading && collectCells) {

                            const linkData = this.getHyperlinkData(rIndex, cIndex);
                            hyperlinksRefs.push({
                                data: linkData,
                                ref
                            });
                        }

                        return (
                            <td
                                ref={ref}
                                key={cell.key}
                                style={cellStyles}
                                className={cellClassNames.join(' ')}
                                data-row-index={rIndex}
                                data-column-index={cIndex}
                            >
                                <div className={divClassNames.join(' ')}>
                                    {cellFactory(cell, measureForCell(descriptors, rIndex, cIndex))}
                                </div>
                            </td>
                        );
                    })}
                </tr>
            )
        });

        if (collectCells) {
            hyperlinksCollector('body', hyperlinksRefs);
        }

        return (
            <>
                {/* // TODO why position is not set in CSS? */}
                <table
                    ref={this.bodyRef}
                    style={{ left: this.props.offsetLeft, top: this.props.offsetTop, position: 'absolute' }}
                    className="jr-mDatatable jr-mDatatableCrosstab jr"
                >
                    <tbody className="jr-mDatatable-body jr">
                        {dataRows}
                    </tbody>
                </table>

                {empty ? <AdHocMessageComponent /> : ''}
            </>
        )
    }
}
